/**
 * Provider Service
 * API integration for provider CRUD operations
 */
import apiClient from './apiClient';
import {
  Provider,
  ProviderFilters,
  PaginatedProvidersResponse,
  ProviderDocument,
  ProviderDocumentType,
  ProviderStats,
  ProviderWeeklySchedule,
} from '../types/provider';

// Re-export for convenience
export * from '../types/provider';

export interface ProviderListParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Partial<ProviderFilters>;
}

// Placeholder for create/update types, can be expanded later
export type CreateProviderRequest = Omit<Provider, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateProviderRequest = Partial<CreateProviderRequest>;

class ProviderService {
  private baseUrl = '/providers';

  /**
   * Get paginated list of providers
   */
  async getProviders(params: ProviderListParams = {}): Promise<PaginatedProvidersResponse> {
    const { page = 1, limit = 10, search, filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (filters) {
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.providerType) queryParams.append('providerType', filters.providerType);
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.city) queryParams.append('city', filters.city);
      if (filters.apenasEmbrionarias) queryParams.append('apenasEmbrionarias', 'true');
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);
    
    // response.data = { providers: Provider[], total, page, limit, totalPages }
    const backendData = response.data;
    
    return {
      message: 'Prestadores listados com sucesso',
      data: backendData.providers ?? [],
      pagination: {
        total: backendData.total ?? 0,
        page: backendData.page || page,
        limit: backendData.limit || limit,
        totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
      },
    };
  }

  /**
   * Contagens do cadastro de prestadores.
   *
   * É daqui que a listagem sabe se existe alguma clínica prospectada: a tela conta, em vez de
   * perguntar ao navegador se a integração está ligada.
   */
  async getStats(): Promise<ProviderStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data.data || response.data;
  }

  /**
   * Get provider by ID
   */
  async getProviderById(id: string): Promise<Provider> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new provider
   */
  async createProvider(data: CreateProviderRequest): Promise<Provider> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update existing provider
   */
  async updateProvider(id: string, data: UpdateProviderRequest): Promise<Provider> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete provider
   */
  async deleteProvider(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Toggle provider active status
   */
  async toggleProviderStatus(id: string): Promise<Provider> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response.data;
  }

  /**
   * Get providers for dropdown (simplified list)
   */
  async getProvidersForDropdown(): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get(`${this.baseUrl}/dropdown`);
    return response.data.data || response.data;
  }

  /**
   * Export providers to Excel
   */
  async exportProviders(filters?: Partial<ProviderFilters>): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(`${this.baseUrl}/export?${queryParams}`, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  // ============================================================
  // Provider Document Methods
  // ============================================================

  /**
   * Get all documents for a provider
   */
  async getDocuments(providerId: string | number): Promise<{
    responsibleName: string | null;
    responsibleCRM: string | null;
    documents: ProviderDocument[];
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${providerId}/documents`);
    return response.data.data || response.data;
  }

  /**
   * Bulk upsert documents + responsible fields
   */
  async saveDocuments(
    providerId: string | number,
    data: {
      responsibleName?: string;
      responsibleCRM?: string;
      documents: Array<{
        documentType: ProviderDocumentType;
        documentNumber?: string;
        validityDate?: string;
      }>;
    }
  ): Promise<any> {
    const response = await apiClient.put(`${this.baseUrl}/${providerId}/documents`, data);
    return response.data;
  }

  /**
   * Upload a document file
   */
  async uploadDocumentFile(
    providerId: string | number,
    documentType: ProviderDocumentType,
    file: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `${this.baseUrl}/${providerId}/documents/${documentType}/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * Delete a document file
   */
  async deleteDocumentFile(
    providerId: string | number,
    documentType: ProviderDocumentType
  ): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${providerId}/documents/${documentType}/file`);
  }

  /**
   * Get document file download URL
   */
  getDocumentFileUrl(providerId: string | number, documentType: ProviderDocumentType): string {
    return `${this.baseUrl}/${providerId}/documents/${documentType}/file`;
  }

  // ============================================================
  // Provider Weekly Schedule Methods
  // ============================================================

  async getWeeklySchedule(providerId: string | number): Promise<ProviderWeeklySchedule[]> {
    const response = await apiClient.get(`${this.baseUrl}/${providerId}/weekly-schedule`);
    return response.data.data || response.data;
  }

  async saveWeeklySchedule(
    providerId: string | number,
    schedules: Omit<ProviderWeeklySchedule, 'id' | 'providerId' | 'isActive'>[]
  ): Promise<ProviderWeeklySchedule[]> {
    const response = await apiClient.put(`${this.baseUrl}/${providerId}/weekly-schedule`, {
      schedules,
    });
    return response.data.data || response.data;
  }
}

export const providerService = new ProviderService();
